import { useNavigate, useOutletContext } from 'react-router-dom';
import { PracticeInfo, SignupError } from '../../components/signup';
import { PracticeInfoFormData, SignupOutletContext } from '../../components/signup/types';
import { SignupType, canSetUpAccount } from '../../components/signup/utils';
import * as Sentry from '@sentry/react';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { useFlowType } from '../../hooks/useFlowType';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { Specialty } from '../../types';
import { UNSUPPORTED_SPECIALTIES } from '../../constants';

const PracticeInfoPage = () => {
  const navigate = useNavigate();
  const signupId = localStorage.getItem('signupId');
  const { formState, updateFormState } = useOutletContext<SignupOutletContext>();
  const { data: featureFlags, isLoading: isLoadingFeatureFlags } = useFeatureFlags();
  const flowType = useFlowType();

  const isDemoFlow = flowType === 'demo';

  const handleNext = (computedSignupType: SignupType, specialty: Specialty) => {
    const basePath = `/${flowType}`;

    // Enterprise users are required to schedule a demo before accessing the portal
    if (
      (isDemoFlow || computedSignupType === 'enterprise') &&
      !UNSUPPORTED_SPECIALTIES.includes(specialty as Specialty as (typeof UNSUPPORTED_SPECIALTIES)[number])
    ) {
      navigate(`${basePath}/schedule-demo`);
    } else if (canSetUpAccount(featureFlags, computedSignupType, specialty)) {
      // If user can set up account, continue to email verification
      navigate('/signup/verification-email', { replace: true });
    } else {
      // If user cannot set up account, stop at success page (manual triage required)
      navigate(`${basePath}/success`);
    }
  };

  const setSignupType = (signupType: SignupType) => {
    localStorage.setItem('signupType', signupType);
  };

  const handleFormDataUpdate = (updates: Partial<PracticeInfoFormData>) => {
    updateFormState(updates);
  };

  if (!signupId) {
    Sentry.captureException('[PracticeInfoPage] No signup ID found in local storage');
    return <SignupError />;
  }

  if (isLoadingFeatureFlags) {
    return <LoadingSpinner />;
  }

  return (
    <PracticeInfo
      onNext={handleNext}
      signupId={signupId}
      email={formState.email}
      setSignupType={setSignupType}
      defaultValues={{
        specialty: formState.specialty,
        specify_other_specialty: formState.specify_other_specialty || '',
        size_numeric: formState.size_numeric,
        ehr: formState.ehr,
        specify_other_ehr: formState.specify_other_ehr || '',
        organization_type: formState.organization_type,
        heard_about_from: formState.heard_about_from,
        specify_other_heard_about_from: formState.specify_other_heard_about_from || '',
        practice_website: formState.practice_website || '',
      }}
      onFormDataUpdate={handleFormDataUpdate}
      hubspotBaseData={formState}
      isDemoFlow={isDemoFlow}
    />
  );
};

export default PracticeInfoPage;
